import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

export interface KnowledgeInterface {
  name: string
  url: string
  background: string
  color?: string
  category: {
    [key: string]: string
  }
  description: {
    [key: string]: string
  }
}

const useKnowledgeStore = defineStore('knowledge', {
  state: (): { knowledge: KnowledgeInterface[] } => {
    return {
      knowledge: [
        {
          name: 'HTML5',
          url: 'https://dev.w3.org/html5/spec-LC/',
          background: '#E86328',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Lenguaje de marcado estándar para la estructura y contenido de páginas web.',
            en: 'The standard markup language for structuring and presenting content on the web.'
          }
        },
        {
          name: 'CSS3',
          url: 'https://www.w3.org/Style/CSS/Overview.en.html',
          background: '#2662E9',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Hoja de estilos utilizada para el diseño visual y la presentación de sitios web.',
            en: 'A stylesheet language used for visual design and presentation of web pages.'
          }
        },
        {
          name: 'Sass',
          url: 'https://sass-lang.com/',
          background: '#CE649A',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Preprocesador de CSS que permite escribir estilos de manera más eficiente y mantenible.',
            en: 'A CSS preprocessor that allows writing styles more efficiently and maintainably.'
          }
        },
        {
          name: 'Tailwind',
          url: 'https://tailwindcss.com/',
          background: '#38BDF9',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Framework CSS utilitario que permite crear interfaces de usuario rápidas y personalizables.',
            en: 'A utility-first CSS framework that enables fast and customizable UI development.'
          }
        },
        {
          name: 'Bootstrap',
          url: 'https://getbootstrap.com/',
          background: '#7533F9',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'A CSS framework that simplifies the creation of responsive and mobile-first websites.',
            en: 'A CSS framework that simplifies the creation of responsive and mobile-first websites.'
          }
        },
        {
          name: 'JavaScript',
          url: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript',
          background: '#EFD81A',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Lenguaje de programación que permite añadir interactividad a las páginas web.',
            en: 'A programming language that adds interactivity to web pages.'
          }
        },
        {
          name: 'Jquery',
          url: 'https://jquery.com/',
          background: '#0968AD',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Biblioteca de JavaScript que simplifica la manipulación del DOM y el manejo de eventos.',
            en: 'A JavaScript library that simplifies DOM manipulation and event handling.'
          }
        },
        {
          name: 'TypeScript',
          url: 'https://www.typescriptlang.org/',
          background: '#3077C6',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Superset de JavaScript que añade tipos estáticos, mejorando la seguridad y el mantenimiento del código.',
            en: 'A superset of JavaScript that adds static types, improving code safety and maintainability.'
          }
        },
        {
          name: 'Vuejs',
          url: 'https://vuejs.org/',
          background: '#41B883',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Framework progresivo de JavaScript para la construcción de interfaces de usuario interactivas.',
            en: 'A progressive JavaScript framework for building interactive user interfaces.'
          }
        },
        {
          name: 'Nuxt',
          url: 'https://nuxt.com/',
          background: '#00DC81',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Framework basado en Vue.js que facilita la creación de aplicaciones web universales y optimizadas.',
            en: 'A framework based on Vue.js that facilitates the creation of universal and optimized web applications.'
          }
        },
        {
          name: 'VueUse',
          url: 'https://vueuse.org/',
          background: '#3FAA7E',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Colección de composables útiles para Vue.js, facilitando la reutilización de lógica.',
            en: 'A collection of useful composables for Vue.js, making logic reuse easier.'
          }
        },
        {
          name: 'Pinia',
          url: 'https://pinia.vuejs.org/',
          background: '#FFD759',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Sistema de gestión de estado para Vue.js, más moderno y mejorado que Vuex.',
            en: 'A state management system for Vue.js, more modern and improved than Vuex.'
          }
        },
        {
          name: 'Vitest',
          url: 'https://vitest.dev/',
          background: '#FCC72B',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Framework de pruebas para aplicaciones Vue.js, similar a Jest pero optimizado para el ecosistema Vue.',
            en: 'A testing framework for Vue.js applications, similar to Jest but optimized for the Vue ecosystem.'
          }
        },
        {
          name: 'Vuex',
          url: 'https://vuex.vuejs.org/',
          background: '#3FAF7C',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Librería oficial de gestión de estado para aplicaciones Vue.js.',
            en: 'The official state management library for Vue.js applications.'
          }
        },
        {
          name: 'Firebase',
          url: 'https://firebase.google.com/',
          background: '#FFCB2D',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Plataforma de Google para desarrollar aplicaciones web y móviles con funcionalidades backend como bases de datos en tiempo real y autenticación.',
            en: 'A Google platform for developing web and mobile apps with backend functionalities like real-time databases and authentication.'
          }
        },
        {
          name: 'Storybook',
          url: 'https://storybook.js.org/',
          background: '#FF4685',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Herramienta para construir y documentar componentes de UI de forma aislada.',
            en: 'A tool for building and documenting UI components in isolation.'
          }
        },
        {
          name: 'Jest',
          url: 'https://jestjs.io/',
          background: '#99425B',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Framework de pruebas unitarias para JavaScript, comúnmente usado con React y Vue.js.',
            en: 'A unit testing framework for JavaScript, commonly used with React and Vue.js.'
          }
        },
        {
          name: 'Cypress',
          url: 'https://www.cypress.io/',
          background: '#7AD8B1',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Herramienta de testing end-to-end que automatiza pruebas en navegadores.',
            en: 'An end-to-end testing tool that automates browser tests.'
          }
        },
        {
          name: 'Vue Test Utils',
          url: 'https://test-utils.vuejs.org/',
          background: '#5672CD',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Utilidad oficial para realizar pruebas unitarias en componentes Vue.js.',
            en: 'The official utility for unit testing Vue.js components.'
          }
        },
        {
          name: 'Webpack',
          url: 'https://webpack.js.org/',
          background: '#1F72B3',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Herramienta para empaquetar módulos JavaScript y otros recursos estáticos.',
            en: 'A tool for bundling JavaScript modules and other static assets.'
          }
        },
        {
          name: 'POEDITOR',
          url: 'https://poeditor.com/',
          background: '#00CCCC',
          category: { es: 'Otros', en: 'others' },
          description: {
            es: 'Herramienta para la localización y traducción de aplicaciones.',
            en: 'A tool for bundling JavaScript modules and other static assets.'
          }
        },
        {
          name: 'PHP',
          url: 'https://www.php.net/',
          background: '#4F5B93',
          category: { es: 'backend', en: 'backend' },
          description: {
            es: 'Lenguaje de programación de scripts del lado del servidor.',
            en: 'A server-side scripting language.'
          }
        },
        {
          name: 'Laravel',
          url: 'https://laravel.com/',
          background: '#F03F30',
          category: { es: 'backend', en: 'backend' },
          description: {
            es: 'Framework de PHP para construir aplicaciones web robustas y escalables.',
            en: 'A PHP framework for building robust and scalable web applications.'
          }
        },
        {
          name: 'Git',
          url: 'https://git-scm.com/',
          background: '#F34C28',
          category: { es: 'control', en: 'control' },
          description: {
            es: 'Sistema de control de versiones distribuido que permite rastrear cambios en el código.',
            en: 'A distributed version control system that tracks changes in code.'
          }
        },
        {
          name: 'Clevertap',
          url: 'https://clevertap.com/',
          background: '#F14444',
          category: { es: 'analitica', en: 'analytics' },
          description: {
            es: 'Herramientas de análisis y engagement para aplicaciones móviles y web.',
            en: 'Analytics and engagement tools for mobile and web applications.'
          }
        },
        {
          name: 'Google Tag Manager',
          url: 'https://marketingplatform.google.com/intl/es/about/tag-manager/',
          background: '#4285F4',
          category: { es: 'analitica', en: 'analytics' },
          description: {
            es: 'Herramienta que permite gestionar etiquetas de marketing y análisis sin modificar el código.',
            en: 'A tool that allows managing marketing and analytics tags without modifying the code.'
          }
        },
        {
          name: 'Sanity',
          url: 'https://www.sanity.io/',
          background: '#F36458',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'CMS sin cabeza que permite gestionar contenido para aplicaciones web.',
            en: 'A headless CMS that allows managing content for web applications.'
          }
        },
        {
          name: 'Stoplight',
          url: 'https://stoplight.io/',
          background: '#139CF4',
          category: { es: 'backend', en: 'backend' },
          description: {
            es: 'Plataforma para la documentación y creación de APIs, especialmente RESTful.',
            en: 'A platform for documenting and creating APIs, especially RESTful APIs.'
          }
        },
        {
          name: 'Dayjs',
          url: 'https://day.js.org/',
          background: '#FF5F4C',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Librería para manejar y manipular fechas y horas en JavaScript.',
            en: 'A library for handling and manipulating dates and times in JavaScript.'
          }
        },
        {
          name: 'Sentry',
          url: 'https://sentry.io/',
          background: '#452550',
          category: { es: 'analitica', en: 'analytics' },
          description: {
            es: 'Plataforma para monitorear errores y excepciones en aplicaciones.',
            en: 'A platform for monitoring errors and exceptions in applications.'
          }
        },
        {
          name: 'Mock service worker',
          url: 'https://mswjs.io/',
          background: '#FF6A33',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Herramienta para simular API en pruebas y desarrollo.',
            en: 'A tool for mocking APIs in testing and development.'
          }
        },
        {
          name: 'Schema',
          url: 'https://schema.org/',
          background: '#990001',
          category: { es: 'Otros', en: 'others' },
          description: {
            es: 'Estandarización para estructurar datos en el web, mejorando la visibilidad en buscadores.',
            en: 'A standard for structuring data on the web, improving visibility in search engines.'
          }
        },
        {
          name: 'JSON:API',
          url: 'https://jsonapi.org/',
          background: '#0B4E22',
          category: { es: 'Otros', en: 'others' },
          description: {
            es: 'Especificación para construir APIs en formato JSON.',
            en: 'A specification for building APIs in JSON format.'
          }
        },
        {
          name: 'Gitlab',
          url: 'https://about.gitlab.com/',
          background: '#E24329',
          category: { es: 'control', en: 'control' },
          description: {
            es: 'Plataforma basada en Git para la integración continua, despliegue y gestión de proyectos.',
            en: 'A Git-based platform for continuous integration, deployment, and project management.'
          }
        },
        {
          name: 'Cookieyes',
          url: 'https://www.cookieyes.com/',
          background: '#1863DC',
          category: { es: 'Otros', en: 'others' },
          description: {
            es: 'Herramienta para la gestión del consentimiento de cookies, cumpliendo con normativas como GDPR.',
            en: 'A tool for managing cookie consent, complying with regulations like GDPR.'
          }
        },
        {
          name: 'Mixpanel',
          url: 'https://mixpanel.com',
          background: '#8A6DFF',
          category: { es: 'analitica', en: 'analytics' },
          description: {
            es: 'Herramientas de análisis y engagement para aplicaciones móviles y web.',
            en: 'Analytics and engagement tools for mobile and web applications.'
          }
        },
        {
          name: 'npm',
          url: 'https://www.npmjs.com/',
          background: '#DB3417',
          category: { es: 'control', en: 'control' },
          description: {
            es: 'Administrador de paquetes para JavaScript, utilizado para instalar dependencias y herramientas.',
            en: 'A package manager for JavaScript, used for installing dependencies and tools.'
          }
        },
        {
          name: 'Figjam',
          url: 'https://www.figma.com/es-es/figjam/',
          background: '#F24E1E',
          category: { es: 'control', en: 'control' },
          description: {
            es: 'Herramienta colaborativa de diagramación y brainstorming, parte de la suite Figma.',
            en: 'A collaborative diagramming and brainstorming tool, part of the Figma suite.'
          }
        },
        {
          name: 'PageSpeed Insights',
          url: 'https://pagespeed.web.dev',
          background: '#0066FF',
          category: { es: 'analitica', en: 'analytics' },
          description: {
            es: 'Herramienta de Google que analiza el rendimiento y la velocidad de las páginas web.',
            en: 'A Google tool that analyzes the performance and speed of web pages.'
          }
        },
        {
          name: 'Font Awesome',
          url: 'https://docs.fontawesome.com/',
          background: '#538DD7',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Biblioteca de iconos vectoriales que se utilizan para enriquecer visualmente las interfaces.',
            en: 'Icon librarie used to visually enhance user interfaces.'
          }
        },
        {
          name: 'Heroicons',
          url: 'https://heroicons.com/',
          background: '#8B5CF6',
          category: { es: 'frontend', en: 'frontend' },
          description: {
            es: 'Biblioteca de iconos vectoriales que se utilizan para enriquecer visualmente las interfaces.',
            en: 'Icon librarie used to visually enhance user interfaces.'
          }
        }
      ]
    }
  },

  getters: {
    getKnowledge: (state) => {
      return state.knowledge
    },
    getKnowledgeGroupBy: (state) => {
      const { locale } = useI18n()
      let result = state.knowledge.map((p: any) => ({
        ...p,
        category: p.category[locale.value as 'es' | 'en'],
        description: p.description[locale.value as 'es' | 'en']
      }))

      result = result.reduce((x: any, y: any) => {
        ;(x[y.category] = x[y.category] || []).push(y)

        return x
      }, {})

      return result
    }
  }
})

export default useKnowledgeStore
